import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles, Target, Compass, ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import about from "@/assets/about.webp";
import aboutSrcSet from "@/assets/about.webp?w=480;768;1200&format=webp&as=srcset";

export const Route = createFileRoute("/quem-somos")({
  component: QuemSomos,
  head: () => ({
    meta: [
      { title: "Quem Somos — InvestCapital" },
      { name: "description", content: "A InvestCapital representa um grupo de investidores com capitais próprios que compra imóveis a pronto pagamento em Portugal continental." },
      { property: "og:title", content: "Quem Somos — InvestCapital" },
      { property: "og:description", content: "Gestão e investimento imobiliário com capitais próprios: rapidez de decisão, rigor e máxima discrição em Portugal continental." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.investcapital.pt/og-investcapital.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://www.investcapital.pt/og-investcapital.jpg" },
      { property: "og:url", content: "https://www.investcapital.pt/quem-somos" },
    ],
    links: [{ rel: "canonical", href: "https://www.investcapital.pt/quem-somos" }],
  }),

});

function QuemSomos() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>

      <section className="border-b border-border/60 bg-primary-soft/40">
        <div className="mx-auto max-w-6xl px-4 py-16 md:py-20">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary">Quem Somos</p>
          <h1 className="mt-2 font-display text-4xl font-semibold md:text-5xl">Investidores com capitais próprios.</h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Representamos um grupo de investidores que compra imóveis a pronto pagamento em Portugal continental.
          </p>
          <div className="mt-8">
            <Link
              to="/vender-a-minha-casa"
              hash="vender-imovel"
              className="inline-flex items-center gap-2 rounded-full bg-[#22c55e] px-7 py-3.5 text-sm font-bold uppercase tracking-wide text-white shadow-lg transition-transform hover:scale-105 hover:bg-[#16a34a]"
            >
              Vender imóvel
              <ArrowUpRight className="h-5 w-5" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20">
        <div className="grid gap-10 md:grid-cols-2 md:items-center">
          <div className="overflow-hidden rounded-2xl border border-border">
            <img src={about} srcSet={aboutSrcSet} sizes="(max-width: 768px) 100vw, 50vw" alt="Equipa InvestCapital" className="h-full w-full object-cover" width={1200} height={900} loading="lazy" decoding="async" />
          </div>
          <div>
            <h2 className="font-display text-3xl font-semibold">Gestão e investimento imobiliário</h2>
            <div className="mt-5 space-y-4 text-muted-foreground">
              <p>
                A <strong className="text-foreground">InvestCapital</strong> é uma empresa especializada na
                gestão e investimento imobiliário, com atuação em Portugal continental.
              </p>
              <p>
                Distinguimo-nos pela <strong className="text-foreground">capacidade financeira própria</strong> e
                pela rapidez de decisão. Compramos imóveis a pronto pagamento, oferecendo aos proprietários e
                parceiros um processo simples, transparente e sem burocracias ou dependência de financiamento
                bancário.
              </p>
              <p>
                O nosso compromisso é valorizar ativos imobiliários com rigor, eficiência e máxima discrição.
              </p>
            </div>

          </div>
        </div>
      </section>

      <section className="bg-muted/40 py-20">
        <div className="mx-auto max-w-6xl px-4">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-semibold">O que não somos</h2>
            <p className="mt-3 text-muted-foreground">
              Não somos mediadora imobiliária e não somos entidade de crédito.
              Não cobramos comissões e não intermediamos vendas — compramos diretamente com capitais próprios.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { icon: Sparkles, title: "Valores", body: "Transparência, seriedade e rapidez em cada proposta que apresentamos." },
              { icon: Target, title: "Missão", body: "Simplificar a venda do seu imóvel: proposta em 48h úteis e pagamento imediato na escritura." },
              { icon: Compass, title: "Como agimos", body: "Compra a pronto pagamento com capitais próprios, sem intermediários e sem comissões." },
            ].map((v) => (
              <div key={v.title} className="rounded-2xl border border-border bg-card p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary">
                  <v.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      </main>
      <SiteFooter />
    </div>
  );
}
